import { createApi } from '@reduxjs/toolkit/query/react';
import { config } from '../../config/config';
import { baseQueryWithInterceptor } from '../utils/baseQueryWithInterceptor';
import {
  ICompany,
  ICreateCompanyRequest,
  IUpdateCompanyRequest,
  IUpdateStructureRequest,
} from '../../interfaces/company.interface';

const companyApi = createApi({
  reducerPath: 'companyApi',
  baseQuery: baseQueryWithInterceptor(`${config.API_BASE_URL}/company`),
  tagTypes: ['company', 'companyAbout', 'companyStructure', 'user'],

  endpoints: (builder) => ({
    // ==========================================================================
    // Main Company CRUD
    // ==========================================================================

    createCompany: builder.mutation<ICompany, ICreateCompanyRequest>({
      query: (data) => ({
        url: '/',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['company'],
    }),

    getMyCompany: builder.query<ICompany, void>({
      query: () => '/my',
      providesTags: ['company'],
    }),

    getCompanyByUserId: builder.query<ICompany, string>({
      query: (userId) => `/user/${userId}`,
      providesTags: ['company'],
    }),

    getCompanyById: builder.query<ICompany, string>({
      query: (id) => `/${id}`,
      providesTags: ['company'],
    }),

    updateCompany: builder.mutation<
      ICompany,
      { id: string; data: IUpdateCompanyRequest }
    >({
      query: ({ id, data }) => ({
        url: `/${id}`,
        method: 'PUT',
        body: data,
      }),
      invalidatesTags: ['company'],
    }),

    deleteCompany: builder.mutation<ICompany, string>({
      query: (id: string) => ({
        url: `/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['company'],
    }),

    // ==========================================================================
    // Company About
    // ==========================================================================

    createOrUpdateCompanyAbout: builder.mutation<ICompany, FormData>({
      query: (formData: FormData) => ({
        url: '/about',
        method: 'POST',
        body: formData,
      }),
      invalidatesTags: ['company', 'user'],
    }),

    deleteCompanyAboutFile: builder.mutation<ICompany, void>({
      query: () => ({
        url: '/about/file',
        method: 'DELETE',
      }),
      invalidatesTags: ['company', 'user'],
    }),

    deleteCompanyAbout: builder.mutation<ICompany, void>({
      query: () => ({
        url: '/about',
        method: 'POST',
        body: (() => {
          const formData = new FormData();
          formData.append('about', '');
          return formData;
        })(),
      }),
      invalidatesTags: ['company', 'user'],
    }),

    // ==========================================================================
    // Company Structure/Questionnaire
    // ==========================================================================

    // Note: getMyCompany already returns structure fields
    // No need for separate getMyCompanyStructure query

    createOrUpdateCompanyStructure: builder.mutation<
      ICompany,
      IUpdateStructureRequest
    >({
      query: (data) => ({
        url: '/structure',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['company', 'user'],
    }),

    // UNUSED: No component calls this, backend endpoint doesn't exist
    // updateCompanyStructure: builder.mutation<
    //   ICompany,
    //   { id: string; data: IUpdateStructureRequest }
    // >({
    //   query: ({ id, data }) => ({
    //     url: `/structure/${id}`,
    //     method: 'PUT',
    //     body: data,
    //   }),
    //   invalidatesTags: ['companyStructure'],
    // }),
  }),
});

export const {
  // Company CRUD (Read: ONE query for everything, Write: separate mutations)
  useCreateCompanyMutation,
  useGetMyCompanyQuery,
  useGetCompanyByUserIdQuery,
  useGetCompanyByIdQuery,
  useUpdateCompanyMutation,
  useDeleteCompanyMutation,

  // Company About (Write only - read via useGetMyCompanyQuery)
  useCreateOrUpdateCompanyAboutMutation,
  useDeleteCompanyAboutFileMutation,
  useDeleteCompanyAboutMutation,

  // Company Structure (Write only - read via useGetMyCompanyQuery)
  useCreateOrUpdateCompanyStructureMutation,
  // useUpdateCompanyStructureMutation, // REMOVED - unused
} = companyApi;

export default companyApi;
